import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import DICOMReader, { IMeshNode } from "@envistaco/dicom-reader";
import { MeshName } from "../utils/common";
import QWebChannel from "../utils/qwebchanel";
import { NormalMesh, buildMesh } from "../model/Mesh";
import { Comment } from "../model/Comment";
import { CrossSection } from "../model/Measurement/CrossSection";
import { Measurement } from "../model/Measurement/Measurement";
import { AnalysesDataManager } from "../utils/AnalysesDataManager";
import { hexToRgb } from "../utils/hexToRgb";
import { AppInsightsContext } from "@microsoft/applicationinsights-react-js";

export const AnimationState = {
  Stopped: 0,
  Playing: 1,
  Paused: 2,
};

export interface IAppGlobalContext {
  qtContext: any;
  patient: IPatient | null;
  normalMeshes: NormalMesh[];
  setNormalMeshes: React.Dispatch<React.SetStateAction<NormalMesh[]>>;
  activeNormalMeshes: NormalMesh[];
  setActiveNormalMeshes: React.Dispatch<React.SetStateAction<NormalMesh[]>>;
  measurementMeshes: { id: string; visible: boolean }[];
  setMeasurementMeshes?: React.Dispatch<
    React.SetStateAction<{ id: string; visible: boolean }[]>
  >;
  opacityValues: { [key: string]: number };
  setOpacityValues: React.Dispatch<
    React.SetStateAction<{ [key: string]: number }>
  >;
  capturedSnapshots: ISnapshot[];
  setCapturedSnapshots: React.Dispatch<React.SetStateAction<ISnapshot[]>>;
  groupedMeshesByDicomId: {
    [key: string]: { meshes: NormalMesh[]; isActive: boolean };
  } | null;
  setGroupedMeshesByDicomId: React.Dispatch<
    React.SetStateAction<{
      [key: string]: { meshes: NormalMesh[]; isActive: boolean };
    } | null>
  >;
  comments: Comment[];
  setComments: React.Dispatch<React.SetStateAction<Comment[]>>;
  isCommentModeActive: boolean;
  setIsCommentModeActive: React.Dispatch<React.SetStateAction<boolean>>;
  animationState: number;
  setAnimationState: React.Dispatch<React.SetStateAction<number>>;
  analysesDataManager: AnalysesDataManager;
  isMatching: boolean;
  setIsMatching: React.Dispatch<React.SetStateAction<boolean>>;
  isTrueColorOn: boolean;
  setIsTrueColorOn: React.Dispatch<React.SetStateAction<boolean>>;
  isHideCommentsOn: boolean;
  setIsHideCommentsOn: React.Dispatch<React.SetStateAction<boolean>>;
  crossSection: CrossSection | null;
  setCrossSection: React.Dispatch<React.SetStateAction<CrossSection | null>>;
  measurements: Measurement[];
  setMeasurements: React.Dispatch<React.SetStateAction<Measurement[]>>;
  locale: string | null;
  setLocale: React.Dispatch<React.SetStateAction<string | null>>;
  translations: { [key: string]: string };
  areTranslationsLoaded: boolean;
}

export interface ISnapshot {
  snapshot: string;
  saved: boolean;
}
export interface IPatient {
  id: string;
  name: string;
}
export interface IDicomMesh {
  seriesInstanceUID: string;
  acquisitionDateTime: string;
  meshes: IMeshNode[];
}

export const AppGlobalDataContext = createContext<IAppGlobalContext>({
  qtContext: null,
  patient: null,
  normalMeshes: [],
  setNormalMeshes: () => {},
  activeNormalMeshes: [],
  setActiveNormalMeshes: () => {},
  measurementMeshes: [],
  opacityValues: {},
  setOpacityValues: () => {},
  capturedSnapshots: [],
  setCapturedSnapshots: () => {},
  groupedMeshesByDicomId: null,
  setGroupedMeshesByDicomId: () => {},
  comments: [],
  setComments: () => {},
  isCommentModeActive: false,
  setIsCommentModeActive: () => {},
  animationState: AnimationState.Stopped,
  setAnimationState: () => {},
  analysesDataManager: new AnalysesDataManager(),
  isMatching: false,
  setIsMatching: () => {},
  isTrueColorOn: false,
  setIsTrueColorOn: () => {},
  isHideCommentsOn: false,
  setIsHideCommentsOn: () => {},
  crossSection: null,
  setCrossSection: () => {},
  measurements: [],
  setMeasurements: () => {},
  locale: null,
  setLocale: () => {},
  translations: {},
  areTranslationsLoaded: false,
});

const defaultColors = [
  "#4d9960",
  "#cc3d79",
  "#52bbcc",
  "#b26a36",
  "#595ec2",
  "#9c59b2",
];

type StepDataCacher = {
  [name: string]: {
    [step: number]: string;
  };
};

export const AppGlobalDataProvider = ({ children }: any) => {
  const [qtContext, setQtContext] = useState<any>(null);
  const reactPlugin = useContext(AppInsightsContext);
  const [patient, setPatient] = useState<IPatient | null>(null);
  const [allFetchedDicoms, setAllFetchedDicoms] = useState<IDicomMesh[]>([]);
  const [normalMeshes, setNormalMeshes] = useState<NormalMesh[]>([]);
  const [activeNormalMeshes, setActiveNormalMeshes] = useState<NormalMesh[]>(
    []
  );
  const [measurementMeshes, setMeasurementMeshes] = useState<
    { id: string; visible: boolean }[]
  >([]);
  const [groupedMeshesByDicomId, setGroupedMeshesByDicomId] = useState<{
    [key: string]: { meshes: NormalMesh[]; isActive: boolean };
  } | null>(null);
  const [opacityValues, setOpacityValues] = useState<{
    [key: string]: number;
  }>({});
  const [capturedSnapshots, setCapturedSnapshots] = useState<ISnapshot[]>([]);
  const [colorsCount] = useState(defaultColors.length);
  const [loadState, setLoadState] = useState<{
    timeoutId: any;
    dataArr: string[];
    isMeshesReady: boolean;
    isAnalysesDataReady: boolean;
    hostDataCount: number;
  }>({
    timeoutId: null,
    dataArr: [],
    isMeshesReady: false,
    isAnalysesDataReady: false,
    hostDataCount: 0,
  });
  const [comments, setComments] = useState<Comment[]>([]);
  const [isCommentModeActive, setIsCommentModeActive] = useState(false);
  const [animationState, setAnimationState] = useState<number>(
    AnimationState.Stopped
  );
  const [isMatching, setIsMatching] = useState<boolean>(false);
  const [isTrueColorOn, setIsTrueColorOn] = useState(false);
  const [isHideCommentsOn, setIsHideCommentsOn] = useState(false);
  const [crossSection, setCrossSection] = useState<CrossSection | null>(null);
  const [measurements, setMeasurements] = useState<Measurement[]>([]);
  const [locale, setLocale] = useState<string | null>(null);
  const [translations, setTranslations] = useState<{ [key: string]: string }>(
    {}
  );
  const [areTranslationsLoaded, setAreTranslationsLoaded] = useState(false);

  const analysesDataManager = useRef<AnalysesDataManager>(
    new AnalysesDataManager()
  );

  const signalsConnectedRef = useRef(false);
  const hasRequestedDataRef = useRef(false);

  const parseLoadData = useCallback(
    async (dataArr: string[]) => {
      const dicoms: IDicomMesh[] = [];
      dataArr.forEach((data) => {
        if (!data) {
          return;
        }
        let raw: string = "";
        try {
          raw = atob(data);
        } catch (err) {
          console.error(err);
          return;
        }
        const array = new Uint8Array(new ArrayBuffer(raw.length));
        for (let i = 0; i < raw.length; i++) {
          array[i] = raw.charCodeAt(i);
        }
        const dicomReader = new DICOMReader(array.buffer);
        const dcmData = dicomReader.extractData();
        const meshes = dcmData.meshes.filter((mesh) => mesh.meshBuffer);

        // We have to filter mesh parts to have only one mesh part per arch
        let commonMeshes = meshes.filter(
          (mesh) =>
            mesh.meshPart === MeshName.MaxillaryPreScan ||
            mesh.meshPart === MeshName.MandibularPreScan
        );
        if (commonMeshes.length === 0) {
          commonMeshes = meshes.filter(
            (mesh) =>
              mesh.meshPart === MeshName.MaxillaryAnatomy ||
              mesh.meshPart === MeshName.MandibularAnatomy
          );
        }
        if (commonMeshes.length === 0) {
          commonMeshes = meshes.filter(
            (mesh) =>
              mesh.meshPart === MeshName.Maxillary ||
              mesh.meshPart === MeshName.Mandibular
          );
        }
        if (commonMeshes.length === 0) {
          return;
        }
        dicoms.push({
          meshes: commonMeshes,
          acquisitionDateTime: dcmData.acquisitionDateTime,
          seriesInstanceUID: dcmData.seriesInstanceUID,
        });
      });
      setAllFetchedDicoms(dicoms);
    },
    [setAllFetchedDicoms]
  );

  useEffect(() => {
    if (loadState.dataArr.length <= 0) {
      return;
    }
    if (loadState.isMeshesReady && loadState.isAnalysesDataReady) {
      parseLoadData(loadState.dataArr);
      return;
    }
    if (loadState.hostDataCount === 0) {
      if (loadState.timeoutId !== null) {
        clearTimeout(loadState.timeoutId);
      }
      loadState.timeoutId = setTimeout(() => {
        setLoadState((a) => {
          return { ...a, isMeshesReady: true, timeoutId: null };
        });
      }, 5000);
    }
  }, [loadState, parseLoadData]);

  useEffect(() => {
    if (globalThis.qt && !qtContext) {
      new QWebChannel(
        globalThis.qt.webChannelTransport,
        (channel: { objects: { WebContext: any } }) => {
          const ctxt = channel.objects.WebContext;
          setQtContext(ctxt);
          setLocale(ctxt.locale);
        }
      );
    }
  }, [qtContext]);

  const loadTranslations = useCallback(async (locale: string) => {
    try {
      setAreTranslationsLoaded(false);
      const translationFile = await import(`../translate/${locale}.json`);
      setTranslations(translationFile.default);
      setAreTranslationsLoaded(true);
    } catch (error) {
      console.error(`Error loading translations for locale ${locale}:`, error);
      if (locale !== "en_US") {
        loadTranslations("en_US");
      }
    } finally {
      setAreTranslationsLoaded(true);
    }
  }, []);

  useEffect(() => {
    if (locale) {
      loadTranslations(locale);
    }
  }, [locale, loadTranslations]);

  const stepCacherRef = useRef<StepDataCacher>({});

  useEffect(() => {
    if (!qtContext || !areTranslationsLoaded) return;

    analysesDataManager.current.setQtContext(qtContext);
    setPatient({ id: qtContext.patientID, name: qtContext.patientName });

    setLoadState((a) => ({
      ...a,
      hostDataCount: qtContext.dicomList?.length ?? 0,
    }));

    // Handlers MUST be stable for disconnect
    const onDataLoaded = (data: string, name: any) => {
      setLoadState((a) => {
        if (a.isMeshesReady) return a;
        const nextDataArr = [...a.dataArr, data];
        return {
          ...a,
          dataArr: nextDataArr,
          isMeshesReady: nextDataArr.length === a.hostDataCount,
        };
      });
    };

    const onDataLoadedByStep = (
      data: string,
      name: string,
      step: number,
      stepCount: number
    ) => {
      // use ref (no stale closure, no state mutation)
      const curItem = stepCacherRef.current[name] ?? {};
      stepCacherRef.current[name] = { ...curItem, [step]: data };

      const collected = stepCacherRef.current[name];

      if (Object.keys(collected).length === stepCount) {
        let fullData = "";
        for (let i = 0; i < stepCount; i++) fullData += collected[i] ?? "";

        setLoadState((a) => {
          if (a.isMeshesReady) return a;
          const nextDataArr = [...a.dataArr, fullData];
          return {
            ...a,
            dataArr: nextDataArr,
            isMeshesReady: nextDataArr.length === a.hostDataCount,
          };
        });

        delete stepCacherRef.current[name];
      }
    };

    const onAnalysesLoaded = (data: any) => {
      setLoadState((a) => {
        if (a.isAnalysesDataReady) return a;
        analysesDataManager.current.load(data);
        return { ...a, isAnalysesDataReady: true };
      });
    };

    // Connect once
    if (!signalsConnectedRef.current) {
      signalsConnectedRef.current = true;
      qtContext.dataLoaded.connect(onDataLoaded);
      qtContext.dataLoadedByStep?.connect(onDataLoadedByStep);
      qtContext.analysesDataLoaded.connect(onAnalysesLoaded);
    }

    // Request once
    if (!hasRequestedDataRef.current) {
      hasRequestedDataRef.current = true;
      qtContext.requestData();
    }

    return () => {
      qtContext.dataLoaded.disconnect(onDataLoaded);
      qtContext.dataLoadedByStep?.disconnect(onDataLoadedByStep);
      qtContext.analysesDataLoaded.disconnect(onAnalysesLoaded);

      signalsConnectedRef.current = false;
      hasRequestedDataRef.current = false;
      stepCacherRef.current = {};
    };
  }, [qtContext, areTranslationsLoaded]);

  const parseMesh = useCallback(
    async (
      meshes: IMeshNode[],
      fileNumber: number,
      acquisitionDateTime: string,
      scanId: string
    ) => {
      const meshObjects = [];
      fileNumber = fileNumber % colorsCount;
      const color = hexToRgb(defaultColors[fileNumber]);

      for (let mesh of meshes) {
        const meshObj = await buildMesh(
          mesh,
          color,
          acquisitionDateTime,
          scanId
        );
        meshObjects.push(meshObj);
      }
      return meshObjects;
    },
    [colorsCount]
  );

  // Load comments from context
  useEffect(() => {
    if (
      !loadState.isAnalysesDataReady ||
      !loadState.isMeshesReady ||
      normalMeshes.length === 0
    )
      return;

    let loadedComments: Comment[] = [];
    analysesDataManager.current.getComments().forEach((comment) => {
      const parentMesh = normalMeshes.find(
        (mesh) =>
          mesh.scanId === comment.parent && mesh.name === comment.parentCatalog
      );
      if (parentMesh) {
        loadedComments.push(
          new Comment(
            comment.comment,
            comment.position,
            parentMesh,
            new Date(comment.timestamp)
          )
        );
      } else {
        console.error("Failed to retrieve comment parent mesh");
      }
      setComments(loadedComments);
    });
  }, [
    loadState.isAnalysesDataReady,
    loadState.isMeshesReady,
    normalMeshes,
    setComments,
  ]);

  // Save comments to context
  useEffect(() => {
    analysesDataManager.current.updateComments(comments);
  }, [comments]);

  async function runWithPool<T>(
    tasks: (() => Promise<T>)[],
    concurrency: number
  ): Promise<T[]> {
    const results: T[] = [];
    let index = 0;

    const workers = new Array(concurrency).fill(0).map(async () => {
      while (index < tasks.length) {
        const current = index++;
        results[current] = await tasks[current]();
      }
    });

    await Promise.all(workers);
    return results;
  }

  useEffect(() => {
    if (!allFetchedDicoms.length) return;

    const parseAllMeshes = async () => {
      const scanIds = new Set<string>();

      const tasks = allFetchedDicoms.map((IDicom, index) => {
        return async () => {
          const meshes = await parseMesh(
            IDicom.meshes,
            index,
            IDicom.acquisitionDateTime,
            IDicom.seriesInstanceUID
          );
          return meshes;
        };
      });

      //Avoid parsing all the meshes in parallel to not explode memory
      const results = await runWithPool(tasks, 2); // 2 or 3 MAX

      const allMeshes = results.flat();

      allMeshes.forEach((m) => scanIds.add(m.scanId));
      setNormalMeshes(allMeshes);

      if (reactPlugin) {
        reactPlugin.getAppInsights().trackEvent({
          name: "Scans Loaded",
          properties: {
            scanCount: scanIds.size,
          },
        });
      }
    };

    parseAllMeshes();
  }, [allFetchedDicoms, parseMesh, reactPlugin]);

  useEffect(() => {
    const sortedNormalMeshesByDate = normalMeshes.sort(
      (a, b) => Number(b.acquisitionDateTime) - Number(a.acquisitionDateTime)
    );

    const groupsMeshes: {
      [key: string]: { meshes: NormalMesh[]; isActive: boolean };
    } = {};

    sortedNormalMeshesByDate.forEach((mesh) => {
      if (!groupsMeshes[mesh.scanId]) {
        groupsMeshes[mesh.scanId] = { meshes: [], isActive: mesh.isActive };
      }
      groupsMeshes[mesh.scanId].meshes.push(mesh);
    });

    setOpacityValues((prevState) => {
      const meshesOpacity: {
        [key: string]: number;
      } = { ...prevState };

      sortedNormalMeshesByDate.forEach((mesh) => {
        if (meshesOpacity[mesh.id] === undefined) {
          meshesOpacity[mesh.id] = 100;
        }
      });
      return meshesOpacity;
    });
    setActiveNormalMeshes(sortedNormalMeshesByDate.filter((m) => m.isActive));
    setGroupedMeshesByDicomId(groupsMeshes);
  }, [normalMeshes]);

  return (
    <AppGlobalDataContext.Provider
      value={{
        qtContext,
        patient,
        normalMeshes,
        setNormalMeshes,
        activeNormalMeshes,
        setActiveNormalMeshes,
        groupedMeshesByDicomId,
        setGroupedMeshesByDicomId,
        measurementMeshes,
        setMeasurementMeshes,
        opacityValues,
        setOpacityValues,
        capturedSnapshots,
        setCapturedSnapshots,
        comments,
        setComments,
        isCommentModeActive,
        setIsCommentModeActive,
        animationState,
        setAnimationState,
        analysesDataManager: analysesDataManager.current,
        isMatching,
        setIsMatching,
        isTrueColorOn,
        setIsTrueColorOn,
        isHideCommentsOn,
        setIsHideCommentsOn,
        crossSection,
        setCrossSection,
        measurements,
        setMeasurements,
        locale,
        setLocale,
        translations,
        areTranslationsLoaded,
      }}
    >
      {children}
    </AppGlobalDataContext.Provider>
  );
};
